import { computed, Component, effect, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { ItemDataService } from '../services/item-data.service';
import { ORDERED_UNIQUE_TIERS, UniquesTierService } from '../services/tiering/uniques-tier.service';
import { ORDERED_TIERS as CURRENCY_ORDERED_TIERS } from '../services/tiering/currency-tier.service';
import { FlatItemData, Tier, TieredItem, TieredUnique, UniqueItem, UniqueTierType } from '../models/item-data-models';
import { ValueComponent } from '../common/value/value.component';
import { FORCE_FIXED_ECO } from '../const';
import { logEvent } from '../app.config';
import { Filter } from '../models/filter-models';
import { ControlsComponent } from '../controls/controls.component';
import { EconomyService } from '../services/economy.service';
import { Economy, InflationData } from '../models/economy-models';
import { MovedTierSectionComponent } from './moved-tier-section/moved-tier-section.component';
import { BaseComponent } from '../common/base.component';

interface ChangelogEntry {
  title: string;
  version: string;
  date: string;
  images?: string[];
  content: SafeHtml;
}

interface ItemDataVm extends TieredItem {
  image?: string;
  tierColor?: string;
  isHidden: boolean;
  movementAmount?: number;
  movementIcon?: string;
  isNewMovementGroup?: boolean;
}

interface UniqueDataVm extends TieredUnique {
  image?: string;
  tierColor?: string;
  isHidden: boolean;
  tierAbbr?: string;
  movementAmount?: number;
  movementIcon?: string;
  isNewMovementGroup?: boolean;
}

@Component({
  selector: 'app-changelog-tab',
  standalone: true,
  imports: [CommonModule, ValueComponent, ControlsComponent, MovedTierSectionComponent],
  templateUrl: './changelog-tab.component.html',
  styleUrls: ['./changelog-tab.component.scss', '../tier-list-tab/tier-tab-common.scss', './moved-tier-common.scss']
})
export class ChangelogTabComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) filter!: Filter;
  @Input() isBeta: boolean = false;
  @Input() isLegacy: boolean = false;
  @Input() initialVisit: boolean = false;

  changelogEntries: ChangelogEntry[] = [];
  archiveLoaded = false;
  loadingArchive = false;
  itemDataFlat: FlatItemData[] = [];
  unique: UniqueItem[] = [];

  forcedFixedMode = FORCE_FIXED_ECO;
  noCurrencyAndUniqueChanges = false;
  playingSsf = false;
  displayMovedTiers = true;
  showMovedTiersSettingsInfo = false;
  forceLoadLatestValues = false;
  alwaysGroupByBaseType = true;
  currency = this.poe1 ? 'c' : ' ex';
  ORDERED_TIERS = CURRENCY_ORDERED_TIERS;
  ORDERED_UNIQUE_TIERS = ORDERED_UNIQUE_TIERS;

  // Computed signals for currency grouped by movement
  currencyByMovement = computed(() => {
    this.economyService.currencyData();
    const allCurrencyTierList = this.currencyTierService.getDefaultTieredCurrency();
    return this.groupByMovement(
      allCurrencyTierList,
      (item) => this.mapToVm(item),
      (a, b) => (b.value || 0) - (a.value || 0)
    );
  });

  // Computed signals for uniques grouped by movement
  uniquesByMovement = computed(() => {
    this.economyService.uniquesData();
    const allUniquesTierList = this.uniquesTierService.getDefaultTieredUniques();
    return this.groupByMovement(
      allUniquesTierList,
      (item) => this.mapToUniqueVm(item),
      (a, b) => ORDERED_UNIQUE_TIERS.indexOf(b.tier) - ORDERED_UNIQUE_TIERS.indexOf(a.tier)
    );
  });

  // Computed signal for currency grouped by tier
  currencyByTier = computed(() => {
    this.economyService.currencyData();
    const allCurrencyTierList = this.currencyTierService.getDefaultTieredCurrency();
    return this.groupByTier(
      allCurrencyTierList,
      (item) => this.mapToVm(item),
      (items) => items.sort((a, b) => {
        // First sort by movement amount: +2, +1, -1, -2
        const movementA = (a as ItemDataVm).movementAmount || 0;
        const movementB = (b as ItemDataVm).movementAmount || 0;
        if (movementA !== movementB) {
          // Positive values first (descending: 2, 1), then negative (ascending: -1, -2)
          if (movementA > 0 && movementB > 0) {
            return movementB - movementA; // Descending: 2 before 1
          }
          if (movementA > 0 && movementB <= 0) {
            return -1; // Positive before negative
          }
          if (movementA <= 0 && movementB > 0) {
            return 1; // Negative after positive
          }
          // Both negative: less negative (smaller absolute value) first
          return movementB - movementA; // -1 before -2
        }
        // Then sort by value
        return (b.value || 0) - (a.value || 0);
      }),
      CURRENCY_ORDERED_TIERS
    );
  });

  // Computed signal for uniques grouped by tier
  uniquesByTier = computed(() => {
    this.economyService.uniquesData();
    const allUniquesTierList = this.uniquesTierService.getDefaultTieredUniques();
    return this.groupByTier(
      allUniquesTierList,
      (item) => this.mapToUniqueVm(item),
      (items) => items.sort((a, b) => {
        // First sort by movement amount: +2, +1, -1, -2
        const movementA = (a as UniqueDataVm).movementAmount || 0;
        const movementB = (b as UniqueDataVm).movementAmount || 0;
        if (movementA !== movementB) {
          // Positive values first (descending: 2, 1), then negative (ascending: -1, -2)
          if (movementA > 0 && movementB > 0) {
            return movementB - movementA; // Descending: 2 before 1
          }
          if (movementA > 0 && movementB <= 0) {
            return -1; // Positive before negative
          }
          if (movementA <= 0 && movementB > 0) {
            return 1; // Negative after positive
          }
          // Both negative: less negative (smaller absolute value) first
          return movementB - movementA; // -1 before -2
        }
        // Then sort by tier order
        return ORDERED_UNIQUE_TIERS.indexOf(b.tier) - ORDERED_UNIQUE_TIERS.indexOf(a.tier);
      }),
      ORDERED_UNIQUE_TIERS
    );
  });

  // Computed signal for checking if all unique tiers have the same color
  allUniqueTiersHaveSameColor = computed(() => {
    this.economyService.uniquesData();
    const allUniquesTierList = this.uniquesTierService.getDefaultTieredUniques();
    const uniqueTierColors = new Set<string>();

    allUniquesTierList.forEach(item => {
      if (item.movedUp || item.movedDown) {
        const vm = this.mapToUniqueVm(item);
        if (vm.tierColor && vm.tierColor !== 'visible') {
          uniqueTierColors.add(vm.tierColor);
        }
      }
    });

    return uniqueTierColors.size === 1 && uniqueTierColors.has('brown');
  });

  tieringModeMap: Record<string, string> = {
    'Normal': 'Normal',
    'Early': 'Early-game',
    'Strict': 'Strict',
    'VeryStrict': 'Very Strict',
    'Custom': 'Custom',
  }

  economyMap: Record<Economy, string> = {
    'Fixed': 'Fixed (SSF)',
    'League': 'League',
    'LeagueHardcore': 'HC League',
    'Standard': 'Standard',
    'StandardHardcore': 'HC Standard',
  }

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private itemDataService: ItemDataService,
    public economyService: EconomyService,
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
  ) {
    super();

    this.itemDataFlat = this.itemDataService.getFlatItemData();
    this.unique = this.itemDataService.getUniquesList();

    // Effect to update state when economy data changes
    effect(() => {
      this.economyService.currencyData();
      this.economyService.uniquesData();
      this.updateEconomyState();
    });
  }

  mapToVm(item: TieredItem): ItemDataVm {
    const flatItem = this.itemDataService.getFlatItemDataByBaseTypeName(item.name);
    const tierColor = this.getTierColor(item.tier);
    const isHidden = this.getTierHidden(item.tier);
    const movementAmount = item.movedUp ? item.movedUp : (item.movedDown ? -item.movedDown : 0);
    const movementIcon = movementAmount > 0 ? '↑'.repeat(movementAmount) : (movementAmount < 0 ? '↓'.repeat(Math.abs(movementAmount)) : '');
    return {
      ...item,
      image: flatItem?.baseType.image,
      tierColor,
      isHidden,
      movementAmount,
      movementIcon
    };
  }

  mapToUniqueVm(item: TieredUnique): UniqueDataVm {
    const unique = this.unique.find(u => u.baseType === item.baseType);
    const tierColor = this.getUniqueTierColor(item.tier);
    const isHidden = this.getUniqueTierHidden(item.tier);
    const movementAmount = item.movedUp ? item.movedUp : (item.movedDown ? -item.movedDown : 0);
    const movementIcon = movementAmount > 0 ? '↑'.repeat(movementAmount) : (movementAmount < 0 ? '↓'.repeat(Math.abs(movementAmount)) : '');
    return {
      ...item,
      image: unique?.image ? '/img/uniques/' + unique.image : undefined,
      tierColor,
      isHidden,
      tierAbbr: item.tier.substring(0, 1),
      movementAmount,
      movementIcon
    };
  }

  // Helper method to group items by movement
  private groupByMovement<T extends TieredItem | TieredUnique, V>(
    items: T[],
    mapper: (item: T) => V,
    sortFn: (a: V, b: V) => number
  ): {
    increasedTwoTiers: V[];
    increasedOneTier: V[];
    decreasedOneTier: V[];
    decreasedTwoTiers: V[];
  } {
    const increasedTwoTiers = items
      .filter(i => i.movedUp && i.movedUp >= 2)
      .map(mapper)
      .sort(sortFn);

    const increasedOneTier = items
      .filter(i => i.movedUp && i.movedUp === 1)
      .map(mapper)
      .sort(sortFn);

    const decreasedOneTier = items
      .filter(i => i.movedDown && i.movedDown === 1)
      .map(mapper)
      .sort(sortFn);

    const decreasedTwoTiers = items
      .filter(i => i.movedDown && i.movedDown >= 2)
      .map(mapper)
      .sort(sortFn);

    return {
      increasedTwoTiers,
      increasedOneTier,
      decreasedOneTier,
      decreasedTwoTiers
    };
  }

  // Helper method to group items by tier
  private groupByTier<T extends TieredItem | TieredUnique, V, TKey extends Tier | UniqueTierType>(
    items: T[],
    mapper: (item: T) => V,
    sortFn: (items: V[]) => void,
    orderedTiers: readonly TKey[]
  ): Array<[TKey, V[]]> {
    const grouped = new Map<TKey, V[]>();

    items.forEach(item => {
      if (item.movedUp || item.movedDown) {
        const vm = mapper(item);
        const tier = item.tier as TKey;
        if (!grouped.has(tier)) {
          grouped.set(tier, []);
        }
        grouped.get(tier)!.push(vm);
      }
    });

    // Sort items within each tier group
    grouped.forEach((items) => {
      sortFn(items);
      // Mark items where movement amount changes (for column breaks)
      // Skip the first item (index 0) to avoid breaking at the start
      // Normalize movement amounts: >= 2 treated as same, <= -2 treated as same
      const normalizeMovementAmount = (amount: number | undefined): number | undefined => {
        if (amount === undefined) return undefined;
        if (amount >= 2) return 2;
        if (amount <= -2) return -2;
        return amount;
      };
      let previousNormalizedMovement: number | undefined;
      items.forEach((vm, index) => {
        const movementAmount = (vm as ItemDataVm | UniqueDataVm).movementAmount;
        const normalizedMovement = normalizeMovementAmount(movementAmount);
        if (index > 0 && normalizedMovement !== previousNormalizedMovement) {
          (vm as ItemDataVm | UniqueDataVm).isNewMovementGroup = true;
        } else {
          (vm as ItemDataVm | UniqueDataVm).isNewMovementGroup = false;
        }
        previousNormalizedMovement = normalizedMovement;
      });
    });

    // Convert to array and sort by ordered tiers in reverse order
    return Array.from(grouped.entries())
      .sort((a, b) => {
        const indexA = orderedTiers.indexOf(a[0]);
        const indexB = orderedTiers.indexOf(b[0]);
        return indexB - indexA; // Reverse order
      });
  }

  ngOnInit(): void {
    this.fetchChangelog();
    this.updateEconomyState();
  }

  updateEconomyState() {
    this.displayMovedTiers = this.settings.showMovedTiers();
    this.noCurrencyAndUniqueChanges = false;

    if (this.filter.economy === Economy.Fixed) {
      this.displayMovedTiers = false;
      if (this.economyService.lastUpdateType === 'launch') this.playingSsf = true;
    } else if (!this.economyService.previousCurrencyData().size && !this.economyService.previousUniquesData().size) {
      this.displayMovedTiers = false;
      this.noCurrencyAndUniqueChanges = false;
    } else if (!this.economyService.lastUpdateType) {
      this.displayMovedTiers = false;
      this.noCurrencyAndUniqueChanges = true;
    }
  }

  showLatestChanges($event: MouseEvent) {
    $event.preventDefault();
    this.displayMovedTiers = true;
    this.forceLoadLatestValues = true;
    logEvent('Changelog', 'Show moved currency', 'true');
  }

  neverShowMovedCurrency($event: MouseEvent) {
    $event.preventDefault();
    this.settings.setHideMovedCurrencyUniques(true);
    logEvent('Settings', 'Hide moved currency & uniques', 'true');
    this.showMovedTiersSettingsInfo = false;
  }

  fetchChangelog(): void {
    this.http.get(`/changelog${this.poe1 ? '-poe1' : '-poe2'}.md`, { responseType: 'text' }).subscribe({
      next: (markdown) => {
        void this.applyChangelog(markdown, false);
      },
      error: (error) => {
        console.error('Failed to load changelog:', error);
      }
    });
  }

  loadArchive(event: MouseEvent): void {
    event.preventDefault();
    if (this.loadingArchive || this.archiveLoaded) {
      return;
    }
    this.loadingArchive = true;
    this.http.get(`/changelog-archive${this.poe1 ? '-poe1' : '-poe2'}.md`, { responseType: 'text' }).subscribe({
      next: (markdown) => {
        void this.applyChangelog(markdown, true).then(() => {
          this.archiveLoaded = true;
          this.loadingArchive = false;
        });
      },
      error: (error) => {
        console.error('Failed to load changelog archive:', error);
        this.loadingArchive = false;
      }
    });
  }

  private async applyChangelog(markdown: string, append: boolean): Promise<void> {
    const entries = await this.parseChangelog(markdown);
    this.changelogEntries = append ? [...this.changelogEntries, ...entries] : entries;
  }

  private async parseChangelog(markdown: string): Promise<ChangelogEntry[]> {
    // Split the markdown by heading level 2 (##) to get each entry, ensuring not to split by level 3 (###)
    const entries = markdown.split(/(?=^##\s)/m);

    // Process each entry
    const entriesPromises = entries
      .filter(entry => entry.trim().length > 0)
      .map(async entry => {
        const lines = entry.trim().split('\n');
        const titleLine = lines[0].replace(/^##\s/, '').trim();

        // Extract title from the first line
        const title = titleLine;

        // Extract version and date from the second line if it exists
        let version = '';
        let date = '';

        const versionDateLine = lines[2];
        if (versionDateLine) {
          // Split by '-'. If there's only one entry, it's the date. Otherwise, the first is the version and the second is the date.
          const versionDateParts = versionDateLine.split('-');
          if (versionDateParts.length === 1) {
            date = versionDateParts[0].trim();
          }
          if (versionDateParts.length === 2) {
            version = versionDateParts[0].trim();
            date = versionDateParts[1].trim();
          }
        }

        // Extract image URLs from the markdown content
        const imageRegex = /!\[.*?\]\((.*?)\)/g;
        const images: string[] = [];
        const contentToSearch = lines.slice(1).join('\n').trim();
        let match;

        while ((match = imageRegex.exec(contentToSearch)) !== null) {
          if (match[1]) {
            images.push(match[1]);
          }
        }

        // Convert content to HTML (excluding the title line)
        let contentMarkdown = lines.slice(3).join('\n').trim();
        // Remove image references from content
        contentMarkdown = contentMarkdown.replace(/!\[.*?\]\((.*?)\)/g, '');
        let contentHtml = await marked(contentMarkdown, {
          gfm: true,
          breaks: true
        });

        const colorClassMap: Record<string, string> = { c: 'currency-color', n: 'normal-item-color', u: 'unique-item-color' };
        contentHtml = contentHtml.replace(/\{([^|}]+)(?:\|([cnu]))?\}/g, (_, text, modifier) => {
          const cls = colorClassMap[modifier ?? 'c'];
          return `<span class="${cls}">${text}</span>`;
        });

        // Sanitize HTML content
        const safeContent = this.sanitizer.bypassSecurityTrustHtml(contentHtml);

        return {
          title,
          version,
          date,
          images,
          content: safeContent
        };
      });

    return Promise.all(entriesPromises);
  }

  private getTierColor(tier: Tier | undefined): string {
    if (!tier) return 'visible';
    if (tier === Tier.FHidden) return 'hidden';
    const tierCosmetic = this.filter.tierCosmetics.find(t => t.tier === tier);
    return (tierCosmetic?.displayOptions.cosmeticOptions?.highlight && tierCosmetic?.displayOptions.cosmeticOptions?.highlightColor)
      ? tierCosmetic.displayOptions.cosmeticOptions.highlightColor.toLocaleLowerCase()
      : 'visible';
  }

  private getTierHidden(tier: Tier | undefined): boolean {
    if (!tier) return false;
    const tierCosmetic = this.filter.tierCosmetics.find(t => t.tier === tier);
    return (tierCosmetic?.displayOptions.active && tierCosmetic?.displayOptions.hidden) || false;
  }

  private getUniqueTierColor(tier: UniqueTierType | undefined): string {
    if (!tier) return 'visible';
    const tierCosmetic = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === tier);
    return (tierCosmetic?.displayOptions.active && tierCosmetic?.displayOptions.cosmeticOptions?.highlight && tierCosmetic?.displayOptions.cosmeticOptions?.highlightColor)
      ? tierCosmetic.displayOptions.cosmeticOptions.highlightColor.toLocaleLowerCase()
      : 'visible';
  }

  private getUniqueTierHidden(tier: UniqueTierType | undefined): boolean {
    if (!tier) return false;
    const tierCosmetic = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === tier);
    return (tierCosmetic?.displayOptions.active && tierCosmetic?.displayOptions.hidden) || false;
  }

  setGroupingMode(mode: 'movement' | 'tier') {
    this.settings.setMovedTiersGroupingMode(mode);
  }

  getGradientClassForTier(tier: Tier | UniqueTierType, tierColor: string, isUnique: boolean): string {
    // For currency EVisible tier, always use grey
    if (!isUnique && tier === Tier.EVisible) {
      return 'moved-tier-grey';
    }

    // For unique tiers, check if all have same color (brown)
    if (isUnique) {
      if (this.allUniqueTiersHaveSameColor() && tierColor === 'brown') {
        // Map to brightness variants
        if (tier === 'Excellent') return 'moved-tier-unique-excellent';
        if (tier === 'Good') return 'moved-tier-unique-good';
        if (tier === 'Restricted') return 'moved-tier-unique-potential';
        if (tier === 'Potential') return 'moved-tier-unique-potential';
        if (tier === 'Other') return 'moved-tier-unique-other';
      }
      // If colors differ, use actual color
      return `moved-tier-${tierColor}`;
    }

    // For currency, use actual tier color
    return `moved-tier-${tierColor}`;
  }

  getMovementAmount(item: TieredItem | TieredUnique): number {
    if (item.movedUp) return item.movedUp;
    if (item.movedDown) return -item.movedDown;
    return 0;
  }
}
